/**
 * Shared input validation and sanitization utilities
 *
 * Used by exec, run, and other commands that accept user-provided
 * repo names, branch names, and prompts.
 */

import chalk from 'chalk';

/**
 * Validate and sanitize repo URL.
 * Ensures the repo is in a safe format (owner/repo or full GitHub URL).
 * Owner and repo names must be alphanumeric with hyphens/underscores, max 39 chars each.
 */
export function validateRepo(repo: string): string {
  // Remove any shell metacharacters and control characters
  const sanitized = repo.trim().replace(/[;&|`$()<>]/g, '').replace(/[\x00-\x1F\x7F]/g, '');

  // Strict format: owner/repo where both are:
  // - Alphanumeric, hyphens, or underscores only
  // - Cannot start or end with hyphen
  // - Max 39 characters each (GitHub limit)
  const validFormat = /^(?:https:\/\/github\.com\/)?[A-Za-z0-9](?:[A-Za-z0-9_-]{0,37}[A-Za-z0-9])?\/[A-Za-z0-9](?:[A-Za-z0-9._-]{0,37}[A-Za-z0-9])?$/;
  if (!validFormat.test(sanitized)) {
    throw new Error(`Invalid repo format: '${repo}'. Use 'owner/repo' or 'https://github.com/owner/repo' (alphanumeric, hyphens, underscores only)`);
  }

  return sanitized;
}

/**
 * Validate and sanitize branch name.
 * Ensures the branch name is safe for git operations.
 */
export function validateBranch(branch: string): string {
  // Git branch restrictions: no .., no leading/trailing ., no ~, ^, :, ?, *, [, etc.
  const sanitized = branch.trim().replace(/[;&|`$()<>]/g, '');

  // Basic validation - disallow obviously dangerous patterns
  if (/\.\./.test(sanitized)) {
    throw new Error(`Invalid branch name: contains '..'`);
  }

  if (/^[@~]/.test(sanitized)) {
    throw new Error(`Invalid branch name: starts with '@' or '~'`);
  }

  // Max length for git refs
  if (sanitized.length > 256) {
    throw new Error(`Invalid branch name: too long (max 256 characters)`);
  }

  return sanitized;
}

/**
 * Validate and sanitize prompt.
 * Removes or escapes potentially dangerous characters while preserving content.
 */
export function validatePrompt(prompt: string): string {
  // Remove control characters except newlines and tabs
  let sanitized = prompt.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '');

  // Limit prompt length to prevent abuse
  const MAX_PROMPT_LENGTH = 100000; // 100k characters
  if (sanitized.length > MAX_PROMPT_LENGTH) {
    console.warn(chalk.dim(`Warning: Prompt truncated to ${MAX_PROMPT_LENGTH} characters`));
    sanitized = sanitized.substring(0, MAX_PROMPT_LENGTH);
  }

  return sanitized;
}
